import { App as AntApp } from "../../../../../shared/frontend/ui";
import { AppConfigProvider } from "../../../../../shared/frontend/theme/AppConfigProvider";
import { DismissibleHintsProvider } from "../../../../../shared/frontend/components/dismissibleHints";
import { QueryClient,QueryClientProvider } from "@tanstack/react-query";
import { backgroundQueryRefresh } from "../../../../../shared/frontend/system/backgroundQueryRefresh";
import React from "react";
import { RouterProvider } from "react-router-dom";
import { setDayjsLocaleForAppLanguage } from "../dayjs-init";
import { InternalBrowserAutofillGuard } from "../components/InternalBrowserAutofillGuard";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { SettingsListRuntimeRoot } from "../components/SettingsListRuntimeRoot";
import { FormSectionCollapseRoot } from "../components/FormSectionCollapseRoot";
import { setGlobalApiErrorHandler } from "./api/client";
import { logApiErrorToBackend } from "./api/logApiError";
import { AuthProvider,useAuth } from "./auth/useAuth";
import { GlobalStudioContextProvider } from "./studios/useGlobalStudioContext";
import { ApiErrorKeys,isApiErrorKey,isDynamicApiErrorKey } from "./constants/apiErrorKeys";
import {
HTTP_STATUS_FORBIDDEN,
HTTP_STATUS_NOT_FOUND,
HTTP_STATUS_REQUEST_ENTITY_TOO_LARGE,
HTTP_STATUS_SERVICE_UNAVAILABLE,
HTTP_STATUS_UNAUTHORIZED
} from "./constants/httpStatus";
import { ErrorBoundary } from "./errors/ErrorBoundary";
import { GlobalErrorOverlay } from "./errors/GlobalErrorOverlay";
import { pickLang } from "./i18n/i18n";
import { I18nProvider,StoredLanguageProvider,useI18n,useStoredLanguage } from "./i18n/useI18n";
import { JobTrackerProvider } from "./jobs/JobTrackerContext";
import { UiPreferencesProvider } from "./prefs/useUiPreferences";
import { RealtimeProvider } from "./realtime/RealtimeContext";
import { AuthorizationChangedListener } from "./auth/AuthorizationChangedListener";
import { router } from "./router";
import { AppReloadEvents } from "./system/AppReloadEvents";
import { StartupGateOverlay } from "../../../../../shared/frontend/system/StartupGateOverlay";
import { signalAppRuntimeUnavailable } from "../../../../../shared/frontend/system/appRuntimeGate";
import { TenantBackupRestoreOverlay } from "./system/TenantBackupRestoreOverlay";
import { TenantImportBlockedListener } from "./system/TenantImportBlockedListener";
import { TenantImportCommitOverlay } from "./system/TenantImportCommitOverlay";
import { ThemeProvider,useTheme } from "./theme/useTheme";
import { DateInputFormatProvider } from "./units/DateInputFormatContext";
import { useEffectiveUnits } from "./units/useEffectiveUnits";
import { registerVersionCheck } from "./versionCheck";
import { useFeedback } from "../../../../../shared/frontend/feedback/useFeedback";
import { isPublicWidgetPage } from "./utils/publicWidgetRoute";
import { HTTP_SERVER_ERROR_MIN } from "./api/httpStatusRanges";
import { FinanceOperationErrors } from "./operations/FinanceOperationErrors";

const GENERIC_ERROR_MESSAGES: readonly string[] = ["Fehler", "Error"];
const MIN_SPECIFIC_ERROR_MESSAGE_LENGTH = 4;
const FORBIDDEN_MESSAGE_RAW = "forbidden";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: backgroundQueryRefresh,
      retry: false,
      staleTime: 0
    }
  }
});

/** Liefert eine nutzerfreundliche Fehlermeldung je nach fehlgeschlagener Anfrage (URL). */
function apiErrorDisplayMessage(e: { url?: string; message: string; status: number }, tr: (key: string) => string): string {
  // Interne Serverdetails gehören nicht in einen Toast. Die Request-ID bleibt
  // als Support-Referenz sichtbar.
  if (e.status >= HTTP_SERVER_ERROR_MIN) return tr("An error occurred");
  const raw = (e.message ?? "").trim();
  const isGeneric = !raw || GENERIC_ERROR_MESSAGES.includes(raw) || raw.length < MIN_SPECIFIC_ERROR_MESSAGE_LENGTH;
  if (!isGeneric) return e.message;
  const u = (e.url ?? "").toLowerCase();
  // GET source-preview vs POST .../preview (Zielvorschau)
  if (u.includes("source-preview") && !u.includes("source-column")) return tr("Import run source preview error");
  if (u.includes("target-preview") || u.includes("/preview")) return tr("Import run target preview error");
  if (u.includes("source-columns")) return tr("Import run source columns error");
  if (u.includes("source-column-distinct")) return tr("Import run enum source values error");
  return tr("An error occurred");
}

function UserHintScope({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  return (
    <DismissibleHintsProvider storagePrefix="@m1member/dismissed-hints:" userId={auth.info?.accountId || "anonymous"}>
      {children}
    </DismissibleHintsProvider>
  );
}

/** Sets global API error toast; must be rendered inside AntApp and I18nProvider. */
function SetApiErrorToast({ children }: { children: React.ReactNode }) {
  const feedback = useFeedback();
  const { tr } = useI18n();
  const auth = useAuth();
  React.useEffect(() => {
    setGlobalApiErrorHandler((e) => {
      // 401: abgelaufene oder ungültige Session, fehlgeschlagener Login — kein System-Log, kein globaler Fehler-Toast.
      // ApiClient ruft bei 401 onUnauthorized auf (Token wird geleert); RequireAuth leitet zur Anmeldung um.
      // Login-/Admin-Login-Formulare zeigen Meldungen lokal im Formular.
      if (e.status === HTTP_STATUS_UNAUTHORIZED) {
        return;
      }

      // 404: fehlende Ressource (z. B. gelöschte Person, Race beim Umschalten der Route) — kein System-Log, kein globaler Toast.
      if (e.status === HTTP_STATUS_NOT_FOUND) {
        return;
      }

      if (e.status === HTTP_STATUS_FORBIDDEN) {
        try {
          const body = JSON.parse(e.bodyText || "{}") as { errorKey?: string; ErrorKey?: string };
          const errKey = body?.errorKey ?? body?.ErrorKey;
          if (errKey === ApiErrorKeys.TenantImportCommitInProgress) {
            if (!auth.info?.tenantImportCommitInProgress) void auth.refreshMe();
            return;
          }
          if (errKey === ApiErrorKeys.TenantBackupRestoreInProgress) {
            return;
          }
        } catch {
          /* ignore */
        }
      }

      // Technische Details ins Backend-Logging (keine erwarteten Login-/Session-Checks)
      logApiErrorToBackend(e);

      if (e.status === HTTP_STATUS_SERVICE_UNAVAILABLE) {
        signalAppRuntimeUnavailable();
        return;
      }
      const rawApiMsg = (e.message ?? "").trim();
      let displayMsg =
        rawApiMsg && (isApiErrorKey(rawApiMsg) || isDynamicApiErrorKey(rawApiMsg)) ? tr(rawApiMsg) : apiErrorDisplayMessage(e, tr);
      if (e.status === HTTP_STATUS_REQUEST_ENTITY_TOO_LARGE) {
        displayMsg = tr("Request entity too large");
      } else if ((e.message || "").trim().startsWith("<") || (e.message || "").includes("<html")) {
        displayMsg = tr("Server returned error page");
      } else if (e.status === HTTP_STATUS_FORBIDDEN) {
        if (isApiErrorKey(e.message)) {
          displayMsg = tr(e.message);
        } else {
          try {
            const body = JSON.parse(e.bodyText || "{}");
            const errKey = body?.errorKey ?? body?.ErrorKey ?? body?.error ?? body?.Error;
            if (typeof errKey === "string" && isApiErrorKey(errKey)) displayMsg = tr(errKey);
          } catch {
            /* ignore */
          }
        }
        const raw = (displayMsg || "").trim().toLowerCase();
        if (raw === FORBIDDEN_MESSAGE_RAW || raw === "") displayMsg = tr("PermissionDenied");
      }
      const showRef = (e.status ?? 0) >= HTTP_SERVER_ERROR_MIN && Boolean(e.requestId);
      feedback.error(showRef ? `${displayMsg} (Ref: ${e.requestId})` : displayMsg);
    });
    return () => setGlobalApiErrorHandler(null);
  }, [feedback, tr, auth.token, auth.info?.accountId, auth.info?.tenantId, auth.refreshMe]);
  return <>{children}</>;
}

function AppInner({ publicWidget }: { publicWidget: boolean }) {
  const { token } = useTheme();
  const auth = useAuth();
  const [storedLang] = useStoredLanguage();
  const units = useEffectiveUnits();

  React.useEffect(() => {
    if (!publicWidget) registerVersionCheck();
  }, [publicWidget]);


  const appLanguage = React.useMemo(
    () =>
      pickLang({
        unitsLocale: units.locale,
        userLanguage: auth.info?.language || null,
        storedLanguage: storedLang,
        browserLanguage: typeof window !== "undefined" ? navigator.language : ""
      }),
    [auth.info?.language, storedLang, units.locale]
  );

  React.useEffect(() => {
    setDayjsLocaleForAppLanguage(appLanguage);
  }, [appLanguage]);

  return (
    <AppConfigProvider override={{ token }}>
      <AntApp notification={{ placement: "topRight" }} message={{ maxCount: 3 }}>
        <I18nProvider>
        <DateInputFormatProvider units={units}>
        <QueryClientProvider client={queryClient}>
        <SettingsListRuntimeRoot>
        <FormSectionCollapseRoot>
        <SetApiErrorToast>
          {!publicWidget && <FinanceOperationErrors />}
          <TenantImportBlockedListener />
          <ErrorBoundary>
            {!publicWidget && <StartupGateOverlay productKey="m1member" reconcileSession={auth.reconcileSession} />}
            <RealtimeProvider token={auth.token ?? ""} tenantId={auth.info?.tenantId ?? null}>
              <AuthorizationChangedListener />
              <GlobalStudioContextProvider>
              <JobTrackerProvider authTokenGetter={() => auth.token ?? ""}>
                <UserHintScope>
                <InternalBrowserAutofillGuard />
                <React.Suspense fallback={<LoadingOverlay visible scope="placeholder" />}>
                  <RouterProvider router={router} />
                </React.Suspense>
                {!publicWidget && <AppReloadEvents />}
                <TenantBackupRestoreOverlay />
                {auth.isReady && !!auth.token && auth.info?.tenantImportCommitInProgress === true ? <TenantImportCommitOverlay /> : null}
                </UserHintScope>
              </JobTrackerProvider>
              </GlobalStudioContextProvider>
            </RealtimeProvider>
          </ErrorBoundary>
          <GlobalErrorOverlay />
        </SetApiErrorToast>
        </FormSectionCollapseRoot>
        </SettingsListRuntimeRoot>
        </QueryClientProvider>
        </DateInputFormatProvider>
        </I18nProvider>
      </AntApp>
    </AppConfigProvider>
  );
}

export function App() {
  const [publicWidget] = React.useState(isPublicWidgetPage);
  React.useEffect(() => router.subscribe(() => {
    // Ein bewusster Wechsel zwischen Verwaltung und öffentlichem Formular beginnt einen
    // neuen Dokumentkontext. So bleiben keine Verwaltungsmonitore im Formular aktiv.
    if (isPublicWidgetPage() !== publicWidget) window.location.reload();
  }), [publicWidget]);
  return (
    <AuthProvider>
      <UiPreferencesProvider>
        <ThemeProvider>
          <StoredLanguageProvider>
            <AppInner publicWidget={publicWidget} />
          </StoredLanguageProvider>
        </ThemeProvider>
      </UiPreferencesProvider>
    </AuthProvider>
  );
}
